/**
 * Chọn quy cách theo chuỗi lọc (kiểu lọc Excel) cho dòng PO.
 * Các hàm thuần: không gọi mạng, không đụng state.
 */
import { displayCell } from '@/lib/cellDisplay'

/** Dòng giá có ánh xạ theo tên cột (KT1…, Định lượng) và các cột nhập lúc mua. */
export type ChainRule = { id: string; mappings?: Record<string, unknown>; input_names?: string[] }

/** Lựa chọn hiện có: tên cột → giá trị đã chọn. */
export type SheetChoices = Record<string, string>

/** Giá trị ô của dòng giá theo cột, dạng chữ; rỗng nếu chưa có. */
export function cellOf(rule: ChainRule, column: string): string {
  return displayCell(rule.mappings?.[column])
}

/** Cột nhập lúc mua của các dòng giá: không bao giờ là cột lọc, không tự điền. */
export function inputNamesOf(rules: ChainRule[], extra: string[] = []): Set<string> {
  const out = new Set<string>(extra)
  for (const rule of rules) for (const name of rule.input_names ?? []) out.add(name)
  return out
}

/** Dòng giá có khớp mọi lựa chọn không. Lựa chọn trống bỏ qua. */
export function ruleMatchesChoices(rule: ChainRule, choices: SheetChoices, skipColumn?: string): boolean {
  return Object.entries(choices).every(([column, value]) => {
    if (column === skipColumn || !value) return true
    return cellOf(rule, column) === value
  })
}

/** Các dòng giá khớp đủ mọi lựa chọn. */
export function sheetMatchingRules<T extends ChainRule>(rules: T[], choices: SheetChoices): T[] {
  return rules.filter((rule) => ruleMatchesChoices(rule, choices))
}

/** So sánh tự nhiên: số theo giá trị ("8kg" trước "16kg", "15" trước "25/15"). */
export function compareOptions(a: string, b: string): number {
  return a.localeCompare(b, 'vi', { numeric: true, sensitivity: 'base' })
}

/**
 * Giá trị còn hợp lệ cho một cột: lấy từ các dòng khớp các lựa chọn KHÁC (không tính chính cột đó).
 * Ô trống không đưa vào danh sách; danh sách sắp theo thứ tự tự nhiên.
 */
export function sheetOptionsFor<T extends ChainRule>(rules: T[], choices: SheetChoices, column: string, order?: string[]): string[] {
  // Lọc nhiều tầng từ trên xuống: một ô chỉ bị lọc bởi các ô đứng TRƯỚC nó, nên đổi ô trên (vd 70x70x70 sang 90x90x90) không bị ô dưới giữ lại.
  const position = order ? order.indexOf(column) : -1
  const effective: SheetChoices = position < 0 || !order
    ? choices
    : Object.fromEntries(Object.entries(choices).filter(([key]) => {
        const at = order.indexOf(key)
        return at >= 0 && at < position
      }))
  const out: string[] = []
  for (const rule of rules) {
    if (!ruleMatchesChoices(rule, effective, column)) continue
    const value = cellOf(rule, column)
    if (value !== '' && !out.includes(value)) out.push(value)
  }
  return out.sort(compareOptions)
}

/** Sau khi đổi một ô: giữ các ô dưới nếu còn khớp ít nhất một dòng giá, ô nào không còn khớp thì bỏ để chọn lại. */
export function pruneLaterChoices<T extends ChainRule>(rules: T[], choices: SheetChoices, changed: string, order: string[]): SheetChoices {
  const out: SheetChoices = { ...choices }
  const start = order.indexOf(changed)
  if (start < 0) return out
  const kept: SheetChoices = {}
  order.forEach((column, index) => {
    if (index <= start) {
      if (out[column]) kept[column] = out[column]
      return
    }
    const value = out[column]
    if (!value) return
    if (sheetMatchingRules(rules, { ...kept, [column]: value }).length > 0) kept[column] = value
  })
  for (const key of Object.keys(out)) if (!order.includes(key) && out[key]) kept[key] = out[key]
  return kept
}

/** Lựa chọn tương ứng với một dòng giá đã chọn (theo các cột lọc, không lấy cột nhập). */
export function choicesFromRule(rule: ChainRule, columns: string[]): SheetChoices {
  const out: SheetChoices = {}
  const inputs = new Set(rule.input_names ?? [])
  for (const column of columns) {
    if (inputs.has(column)) continue
    const value = cellOf(rule, column)
    if (value !== '') out[column] = value
  }
  return out
}

/**
 * Lý do chặn lưu của dòng bảng tính; null nếu đủ điều kiện. Giá lưu do máy chủ tính lại từ
 * dòng giá đã chọn và số nhập, nên chỉ cần: đã chọn quy cách, đủ ô nhập, tính tự động hoàn tất.
 */
export function sheetSaveBlocker(
  row: { priceRuleId?: string | null; sheetInfo?: { input_names: string[] } | null; sheetInputs?: Record<string, string>; sheetPreview?: unknown },
  label: string,
): string | null {
  if (!row.priceRuleId) return `${label}: chưa chọn quy cách (chọn ô quy cách hoặc bấm Chọn dòng báo giá)`
  const missing = (row.sheetInfo?.input_names ?? []).filter((n) => (row.sheetInputs?.[n] ?? '') === '')
  if (missing.length > 0) return `${label}: chưa nhập ${missing.join(', ')}`
  if (!row.sheetPreview) return `${label}: đang chờ tính giá tự động`
  return null
}

/**
 * Thuộc tính gửi cho dòng bảng tính: ánh xạ của dòng giá (KT, Định lượng), không gửi khóa cũ
 * (type_code, kind…) từ biểu mẫu NCC. Giá do máy chủ tính từ dòng giá và số nhập.
 */
export function sheetLineSpecAttributes(mappings: Record<string, unknown> | undefined | null): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(mappings ?? {})) {
    if (key === 'ĐV đơn giá') continue
    if (value === null || value === undefined || value === '') continue
    out[key] = value
  }
  return out
}
